import {
  BarChart3,
  BookOpen,
  Eye,
  LineChart,
  Network,
  Phone,
  ScanSearch,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Single source of truth for the site's navigation IA, consumed by both
 * `VariantNav` and `Footer` so the header and footer never drift.
 *
 * `href`s point at the canonical live paths from the migration's zero-404 URL
 * list (`src/data/legacy-urls.json`, owned by the redirect/alias map). Paths
 * marked TODO are IA still being decided; they stay `#` so they never mint an
 * orphan URL. When a page lands, flip its `#` here in one place.
 *
 * External destinations (app, docs, GitHub) set `external: true`.
 */
export type NavLink = {
  label: string
  href: string
  external?: boolean
  /** header only: shown from the xl breakpoint up, where the bar has room (the mobile drawer always lists it) */
  wide?: true
}
export type PlatformItem = NavLink & {
  desc: string
  icon?: LucideIcon
  /** image icon (e.g. the clawd mascot) used instead of a Lucide glyph */
  iconSrc?: string
  badge?: string
}

/** Live + indexed; `/agentic-ai-testing` is canonical (`/scenario` is alias-only). */
export const platformMenu: PlatformItem[] = [
  {
    label: 'Langy',
    desc: 'The automated AI engineer: reads your traces, writes the tests, and opens the pull request',
    href: '/langy',
    icon: Sparkles,
    badge: 'New',
  },
  {
    label: 'Instant Evals',
    desc: 'Search your entire production history with on-demand evals. Ask a question, get the matches back in minutes',
    href: '/instant-evals',
    icon: ScanSearch,
    badge: 'New',
  },
  {
    label: 'Agentic AI Testing',
    desc: 'Run realistic user scenarios against your agent to catch issues before production',
    href: '/agentic-ai-testing',
    icon: BookOpen,
  },
  {
    label: 'LLM Evaluation',
    desc: 'Measure response quality and accuracy so you ship agents that hold up in production',
    href: '/llm-evaluation',
    icon: BarChart3,
  },
  {
    label: 'LLM Observability',
    desc: 'Trace every agent step and monitor cost and latency with full production visibility',
    href: '/llm-observability',
    icon: Eye,
  },
  {
    label: 'AI Gateway',
    desc: 'Every AI call through one endpoint with your own keys, on a budget per person, team and customer',
    href: '/gateway',
    icon: Network,
    badge: 'New',
  },
  {
    label: 'Prompt Management',
    desc: 'Version, deploy, and A/B test prompts as code, with full history and GitHub sync',
    href: '/prompt-management',
    icon: LineChart,
  },
  {
    label: 'Voice AI',
    desc: 'Test and simulate your voice AI agents at scale, before they talk to customers',
    href: '/voice-ai-agent-testing',
    icon: Phone,
  },
  {
    label: 'LLM Red-teaming',
    desc: 'Simulated attacks that uncover safety and security gaps in AI agents',
    href: '/llm-red-teaming',
    icon: ShieldCheck,
  },
  {
    label: 'Track your Claude Code Usage',
    desc: 'Full trace history and token spend for Claude Code, Codex, and every coding agent',
    href: '/claude-code-usage',
    iconSrc: '/vendor/claudecode-white.svg',
  },
]

/** Top-level header items. `Platform` opens `platformMenu`; the rest are links. */
export const primaryNav: NavLink[] = [
  { label: 'Platform', href: '#platform' }, // dropdown trigger, see platformMenu
  { label: 'Enterprise', href: '/enterprise' },
  { label: 'Customers', href: '/customers' },
  { label: 'Blog', href: '/blog' },
  { label: 'Benchmarks', href: '/benchmarks' },
  { label: 'Docs', href: 'https://docs.langwatch.ai', external: true },
  { label: 'Pricing', href: '/pricing' },
]

export const headerCtas = {
  github: { label: 'GitHub', href: 'https://github.com/langwatch/langwatch', external: true },
  contactSales: { label: 'Book a demo', href: '/get-a-demo' },
  signIn: { label: 'Sign in', href: 'https://app.langwatch.ai', external: true },
  signUp: { label: 'Sign Up', href: 'https://app.langwatch.ai/', external: true },
} satisfies Record<string, NavLink>

export const footerColumns: { title: string; links: NavLink[] }[] = [
  {
    title: 'Platform',
    links: [
      { label: 'Langy, your AI engineer', href: '/langy' },
      { label: 'Instant Evals', href: '/instant-evals' },
      { label: 'Agent simulation testing', href: '/agentic-ai-testing' },
      { label: 'LLM evaluation', href: '/llm-evaluation' },
      { label: 'LLM observability', href: '/llm-observability' },
      { label: 'AI Gateway', href: '/gateway' },
      { label: 'Prompt management', href: '/prompt-management' },
      { label: 'Track your Claude Code usage', href: '/claude-code-usage' },
      { label: 'Customers', href: '/customers' },
      { label: 'Enterprise', href: '/enterprise' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Feature comparison', href: '/comparison' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Docs', href: 'https://docs.langwatch.ai/introduction', external: true },
      { label: 'Blog', href: '/blog' },
      { label: 'Research', href: '/research' },
      { label: 'Benchmarks', href: '/benchmarks' },
      { label: 'AI Agents Guide', href: '/guides/ai-agent-guide' },
      { label: 'Evals Golden Guide', href: '/guides/evals-guide' },
      { label: 'Agent Governance Playbook', href: '/agent-governance-playbook' },
      { label: 'Changelog', href: '/changelog' },
      { label: 'RSS', href: '/blog/rss.xml' },
      { label: 'Evals training', href: '/ai-agent-testing-training' },
      { label: 'SDKs', href: '/sdks' },
      { label: 'Switch from Langfuse', href: '/langfuse-alternative' },
      { label: 'Switch from Braintrust', href: '/braintrust-alternative' },
      { label: 'Switch from LangSmith', href: '/langsmith-alternative' },
      { label: 'Switch from Arize', href: '/arize-alternative' },
      { label: 'Switch from Humanloop', href: '/humanloop-alternative' },
      { label: 'Better Agents Manifesto', href: '/better-agents-manifesto' },
      { label: 'llms.txt', href: '/llms.txt', external: true },
    ],
  },
  {
    title: 'Integrations',
    links: [
      { label: 'All integrations', href: '/integrations' },
      { label: 'Python SDK', href: 'https://docs.langwatch.ai/integration/python/guide', external: true },
      { label: 'TypeScript SDK', href: 'https://docs.langwatch.ai/integration/typescript/guide', external: true },
      { label: 'Go SDK', href: 'https://langwatch.ai/docs/integration/go/guide', external: true },
      { label: 'OpenTelemetry', href: 'https://docs.langwatch.ai/integration/opentelemetry/guide', external: true },
      { label: 'OpenAI Agents', href: 'https://docs.langwatch.ai/integration/python/integrations/open-ai-agents', external: true },
      { label: 'AWS Bedrock', href: 'https://docs.langwatch.ai/integration/python/integrations/aws-bedrock', external: true },
      { label: 'Azure OpenAI', href: 'https://docs.langwatch.ai/integration/python/integrations/open-ai-azure', external: true },
      { label: 'Vertex AI', href: 'https://docs.langwatch.ai/integration/python/integrations/vertex-ai', external: true },
      { label: 'LangGraph', href: 'https://docs.langwatch.ai/integration/python/integrations/langgraph', external: true },
      { label: 'LangChain', href: 'https://docs.langwatch.ai/integration/python/integrations/langchain', external: true },
      { label: 'CrewAI', href: 'https://docs.langwatch.ai/integration/python/integrations/crew-ai', external: true },
      { label: 'DSPy', href: 'https://docs.langwatch.ai/integration/python/integrations/dspy', external: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', href: '/about-us' },
      { label: 'Careers', href: 'https://langwatchdata.notion.site/langwatchcareers', external: true },
      { label: 'Become a partner', href: '/partners' },
      { label: 'Trust center', href: '/trust-center' },
      { label: 'Status', href: 'https://status.langwatch.ai/', external: true },
      { label: 'Contact', href: 'mailto:contact@langwatch.ai' },
    ],
  },
]

export const legalLinks: NavLink[] = [
  { label: 'Privacy', href: '/legal/privacy-policy' },
  { label: 'Terms', href: '/legal/terms-conditions' },
  { label: 'Trust center', href: '/trust-center' },
  { label: 'Status', href: 'https://status.langwatch.ai/', external: true },
]
